import React, { useState } from 'react';
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography
} from '@mui/material';
import { toPersianDigits, toEnglishDigits } from '../../../../utils/convertNumbers';

const TimePicker = ({ value, onChange, label, error, helperText, ...props }) => {
    const [hour, setHour] = useState('');
    const [minute, setMinute] = useState('');

    // Parse initial value
    React.useEffect(() => {
        if (value) {
            const timeParts = value.split(':');
            if (timeParts.length >= 2) {
                // Convert Persian digits to English for internal state
                setHour(toEnglishDigits(timeParts[0]));
                setMinute(toEnglishDigits(timeParts[1]));
            }
        } else {
            setHour('');
            setMinute('');
        }
    }, [value]);

    const handleHourChange = (newHour) => {
        setHour(newHour);
        // If minute is not set, default to 00 when hour is selected
        const selectedMinute = minute || '00';
        const timeValue = newHour ? `${newHour}:${selectedMinute}` : '';
        onChange(timeValue);
    };

    const handleMinuteChange = (newMinute) => {
        setMinute(newMinute);
        // If hour is not set, don't set time value
        const timeValue = hour && newMinute ? `${hour}:${newMinute}` : (hour ? `${hour}:00` : '');
        onChange(timeValue);
    };

    // Generate hour options (0-23)
    const hourOptions = Array.from({ length: 24 }, (_, i) => {
        const hour = i.toString().padStart(2, '0');
        return { value: hour, label: hour };
    });

    // Generate minute options (0, 15, 30, 45) - 15 minute intervals
    const minuteOptions = Array.from({ length: 4 }, (_, i) => {
        const minute = (i * 15).toString().padStart(2, '0');
        return { value: minute, label: minute };
    });

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                <FormControl sx={{ minWidth: 100 }} error={error} size="small" disabled={props.disabled}>
                    <InputLabel shrink>ساعت</InputLabel>
                    <Select
                        value={hour}
                        onChange={(e) => handleHourChange(e.target.value)}
                        label="ساعت"
                        displayEmpty
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '40px',
                                direction: 'rtl',
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>--</em>
                        </MenuItem>
                        {hourOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {toPersianDigits(option.label)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                
                <Typography variant="h6" sx={{ mx: 1 }}>:</Typography>
                
                <FormControl sx={{ minWidth: 100 }} error={error} size="small" disabled={props.disabled}>
                    <InputLabel shrink>دقیقه</InputLabel>
                    <Select
                        value={minute}
                        onChange={(e) => handleMinuteChange(e.target.value)}
                        label="دقیقه"
                        displayEmpty
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '40px',
                                direction: 'rtl',
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>--</em>
                        </MenuItem>
                        {minuteOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {toPersianDigits(option.label)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            
            {helperText && (
                <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, display: 'block' }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};

export default TimePicker;
